import {
  IconButton,
  StandardTextFieldProps,
  TextField,
  Typography,
} from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import {
  Column,
  Columns,
  Inline,
  OverflowText,
  Stack,
  Tag,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { FormikValues, useField, useFormikContext } from 'formik';
import {
  forwardRef,
  ReactNode,
  UIEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import styled from 'styled-components';
import { CombinedLoadDTO } from '../data/LoadDTO';
import { CloseIcon } from 'shared/icons/CloseIcon';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { LoadOrderWaypoints } from './LoadOrderWaypoints';
import { useCombinedLoads } from '../data/LoadsAPI';
import { ContactDTO } from 'carrier/contacts/data/ContactDTO';
import { FIFTH_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { LoadOptionList } from './OptionsLoadingList';
import { useFlag } from 'shared/settings/FeatureToggles';

interface OrderFieldCommonProps {
  TextFieldProps?: StandardTextFieldProps;
}

const StartIcon = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 8px;
`;

const MultipleOrderFieldText = styled(TextField)`
  & .MuiAutocomplete-inputRoot {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    & .MuiAutocomplete-input {
      width: 100%;
    }
  }
`;

interface OrderFieldMultipleProps extends Omit<
  OrderFieldCommonProps,
  'options' | 'onChange' | 'getOptionLabel'
> {
  startIcon?: ReactNode;
  name: string;
  guid?: string;
  loadNumber?: string;
  onUpdateInitialValue?: (order: CombinedLoadDTO) => void;
}

export const LoadSelectionField = forwardRef(
  (
    {
      startIcon,
      TextFieldProps,
      loadNumber,
      guid,
      name,
      onUpdateInitialValue,
      ...props
    }: OrderFieldMultipleProps,
    ref,
  ) => {
    const isCombinedInvoiceEnabled = useFlag('combined_send_invoice');
    const { setValues } = useFormikContext<FormikValues>();
    const [field, { touched, error }] = useField<CombinedLoadDTO[]>({ name });
    const [broker] = useField<ContactDTO>({ name: 'customer' });
    const [search, setSearch] = useState('');
    const query = useDebouncedValue(search, FIFTH_OF_A_SECOND);

    const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
      useCombinedLoads(
        guid,
        {
          q: query,
          page_size: 25,
          customer_name: broker.value.name || '',
        },
        { enabled: query.length > 0 },
      );

    // Per-item errors make `error` an array of objects, which cannot be rendered.
    const errorMessage = touched && typeof error === 'string' ? error : '';

    const options = useMemo<CombinedLoadDTO[]>(
      () =>
        data?.pages
          ? data.pages
              .flatMap((page) => page.data)
              .filter((load) => load.guid !== guid)
          : [],
      [data, guid],
    );

    const handleTag = (option: CombinedLoadDTO) => {
      openExternalURL(`/tms/loads/${option.guid}`, { target: '_blank' });
    };

    const handleDelete = (option: CombinedLoadDTO) => {
      const filteredOrders = field.value.filter(
        (item) => item.guid !== option.guid,
      );
      void setValues((value) => ({ ...value, orders: filteredOrders }));
    };

    const onScroll = useCallback(
      ({
        currentTarget: { scrollTop, scrollHeight, clientHeight },
      }: UIEvent<HTMLUListElement>) => {
        if (!hasNextPage || isFetchingNextPage) return;

        const threshold = 32 * 5;
        const delta = scrollHeight - scrollTop - threshold;

        if (delta <= clientHeight) {
          fetchNextPage();
        }
      },
      [hasNextPage, fetchNextPage, isFetchingNextPage],
    );

    return (
      <Autocomplete
        {...props}
        options={options}
        ref={ref}
        multiple={true}
        freeSolo={false}
        disableClearable={true}
        filterSelectedOptions={true}
        aria-label="load-list"
        disabled={!isCombinedInvoiceEnabled}
        loading={isFetching}
        value={field.value}
        ListboxComponent={LoadOptionList}
        ListboxProps={{
          onScroll,
          'aria-busy': isFetchingNextPage,
        }}
        getOptionSelected={(option, value) => option.guid === value.guid}
        getOptionLabel={(option) => option.number}
        filterOptions={(filterOptions) => {
          return filterOptions.filter((option) => option.number !== '');
        }}
        onChange={(_, selectedValue) => {
          void setValues((value) => ({ ...value, orders: selectedValue }));
        }}
        renderTags={(value) => {
          return (
            <Inline space="xxsmall">
              {value.length > 0 &&
                value.map((option, index) => {
                  return (
                    <Tag color="grey" variant="subtle">
                      <Box display="flex">
                        <Typography color="textPrimary">
                          {option.number || 'No Load ID'}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            handleTag(option);
                          }}
                        >
                          <OpenInNew color="action" fontSize="small" />
                        </IconButton>
                        {index !== 0 && (
                          <IconButton
                            onClick={() => {
                              handleDelete(option);
                            }}
                            size="small"
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Tag>
                  );
                })}
            </Inline>
          );
        }}
        renderOption={(option) => {
          return (
            <Stack>
              <Columns>
                <Column width="fluid">
                  <Columns space="xxsmall" align="center">
                    <Column width="content">
                      <Typography color="textSecondary" variant="h6">
                        LOAD ID
                      </Typography>
                    </Column>
                    <Column width="adaptive">
                      <OverflowText
                        gutterBottom={false}
                        disableUnderline={true}
                        TooltipProps={{ placement: 'top', interactive: true }}
                      >
                        {option.number}
                      </OverflowText>
                    </Column>
                  </Columns>
                </Column>

                <Column width="content">
                  <Inline horizontalAlign="right" space="xsmall">
                    {option.invoice_date && (
                      <Tag variant="subtle" color="grey">
                        Invoiced
                      </Tag>
                    )}
                    {option.is_paid && (
                      <Tag variant="subtle" color="grey">
                        Paid
                      </Tag>
                    )}
                    <Tag
                      variant="subtle"
                      color={getTagColor(option.status_display)}
                    >
                      {option.status_display}
                    </Tag>
                  </Inline>
                </Column>
              </Columns>

              {option.vehicles_count && option.vehicles_count > 0 && (
                <Typography variant="h5">
                  {option.vehicles_count}{' '}
                  {formatPlural(option.vehicles_count, 'vehicle', 'vehicles')}
                </Typography>
              )}
              <Stack>
                <LoadOrderWaypoints load={option} />
              </Stack>
            </Stack>
          );
        }}
        renderInput={(params) => (
          <MultipleOrderFieldText
            {...params}
            {...TextFieldProps}
            data-start-icon={String(!!startIcon)}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="Enter load ids to combine them into one invoice"
            multiline={true}
            minRows={2}
            InputProps={{
              ...TextFieldProps?.InputProps,
              ...params.InputProps,
              startAdornment: (
                <>
                  {startIcon && <StartIcon>{startIcon}</StartIcon>}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            variant="outlined"
            fullWidth={true}
            error={!!errorMessage}
            helperText={errorMessage}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Backspace' && field.value.length === 1) {
                event.stopPropagation();
              }
            }}
          />
        )}
      />
    );
  },
);

LoadSelectionField.displayName = 'LoadSelectionField';

function getTagColor(statusDisplay: string) {
  switch (statusDisplay) {
    case 'New':
      return 'teal';
    case 'Picked up':
      return 'yellow';
    default:
      return 'green';
  }
}
